import type { Avatar, GameSummary, LeaderboardRow, WorldObject } from '../types'

export const DEFAULT_AVATAR: Avatar = {
  skin: '#D99B72',
  hair: '#18233C',
  shirt: '#6C8CFF',
  pants: '#273A6B',
  shoes: '#F3C969',
  accessory: 'Halo ring',
}

export const AVATAR_OPTIONS = {
  skin: [
    { label: 'Sunstone', value: '#D99B72' },
    { label: 'Copper', value: '#A85F42' },
    { label: 'Moonlit', value: '#F0C7A7' },
    { label: 'Cocoa', value: '#6B3F32' },
  ],
  hair: [
    { label: 'Night wave', value: '#18233C' },
    { label: 'Cobalt crop', value: '#2D4FCE' },
    { label: 'Mint buzz', value: '#74E6C1' },
    { label: 'Solar cap', value: '#F3C969' },
  ],
  shirt: [
    { label: 'Azure', value: '#6C8CFF' },
    { label: 'Mint signal', value: '#74E6C1' },
    { label: 'Coral pulse', value: '#FF8E83' },
    { label: 'Violet orbit', value: '#B49CFF' },
  ],
  pants: [
    { label: 'Deep tide', value: '#273A6B' },
    { label: 'Space black', value: '#161D33' },
    { label: 'Cloud grey', value: '#AAB7D6' },
  ],
  shoes: [
    { label: 'Sunbeam', value: '#F3C969' },
    { label: 'Signal white', value: '#F5F7FF' },
    { label: 'Coral sole', value: '#FF8E83' },
  ],
  accessory: [
    { label: 'Halo ring', value: 'Halo ring' },
    { label: 'Signal antenna', value: 'Signal antenna' },
    { label: 'Cloud cape', value: 'Cloud cape' },
    { label: 'None', value: 'None' },
  ],
} as const

export const GAMES: GameSummary[] = [
  {
    id: 'sky-island-adventure',
    name: 'Sky Island Adventure',
    description: 'Leap between pocket-sized floating worlds, bank your first coins, and light up the next checkpoint.',
    creator: 'BlockVerse Studio',
    category: 'Adventure',
    players: 1284,
    likes: 9821,
    accent: '#6C8CFF',
    thumbnail: 'sky',
    playable: true,
    featured: true,
  },
  {
    id: 'neon-drift',
    name: 'Neon Drift Circuit',
    description: 'A midnight sprint through a modular light-ribbon city.',
    creator: 'Orbit Club',
    category: 'Racing',
    players: 842,
    likes: 4310,
    accent: '#74E6C1',
    thumbnail: 'race',
  },
  {
    id: 'tiny-titans',
    name: 'Tiny Titans Arena',
    description: 'Build a pocket champion and outsmart the arena clock.',
    creator: 'Mossbyte',
    category: 'Simulation',
    players: 391,
    likes: 2287,
    accent: '#FF8E83',
    thumbnail: 'arena',
  },
  {
    id: 'moonbase-09',
    name: 'Moonbase 09',
    description: 'A cooperative survival outpost at the edge of the quiet side.',
    creator: 'Kite Foundry',
    category: 'Survival',
    players: 214,
    likes: 1642,
    accent: '#B49CFF',
    thumbnail: 'moon',
  },
  {
    id: 'cloudstep',
    name: 'Cloudstep Trials',
    description: 'A vertical parkour study in rhythm, momentum, and soft landings.',
    creator: 'Blue Hour',
    category: 'Obby / Parkour',
    players: 188,
    likes: 1218,
    accent: '#F3C969',
    thumbnail: 'cloud',
  },
]

export const WORLD_PLATFORMS = [
  { x: 0, y: 0, z: 0, width: 18, depth: 18, color: '#22345f' },
  { x: 13, y: 3, z: -3, width: 8, depth: 8, color: '#2c477a' },
  { x: -13, y: 5, z: 4, width: 7, depth: 7, color: '#304c82' },
  { x: 0, y: 2, z: -13, width: 5, depth: 10, color: '#36558f' },
] as const

export const WORLD_BRIDGES = [
  { x: 6.5, y: 1.3, z: 1.2, width: 9, depth: 1.1, color: '#7c5e52' },
  { x: -6.4, y: 2.8, z: 2.3, width: 7, depth: 1.1, color: '#7c5e52' },
] as const

export const WORLD_COINS = [
  { id: 'coin-1', position: [-5, 1.3, 1] as [number, number, number] },
  { id: 'coin-2', position: [3, 1.3, -4] as [number, number, number] },
  { id: 'coin-3', position: [12.8, 4.5, -4] as [number, number, number] },
  { id: 'coin-4', position: [-13, 6.5, 4] as [number, number, number] },
  { id: 'coin-5', position: [0, 3.5, -13] as [number, number, number] },
] as const

export const WORLD_CHECKPOINTS = [
  { id: 'checkpoint-1', name: 'Launchpad', position: [0, 1.15, 7] as [number, number, number] },
  { id: 'checkpoint-2', name: 'Blue Arc', position: [12.8, 4.15, -3] as [number, number, number] },
  { id: 'checkpoint-3', name: 'North Cloud', position: [0, 3.15, -13] as [number, number, number] },
] as const

export const OTHER_PLAYERS = [
  { name: 'MiraMint', position: [-3, 1.2, -2] as [number, number, number], color: '#74E6C1' },
  { name: 'OrbitJay', position: [5, 1.2, 3] as [number, number, number], color: '#B49CFF' },
  { name: 'SolaByte', position: [12.8, 4.2, -5] as [number, number, number], color: '#FF8E83' },
]

export const DEMO_LEADERBOARD: LeaderboardRow[] = [
  { rank: 1, username: 'MiraMint', coins: 48, score: '04:12', avatar: { ...DEFAULT_AVATAR, shirt: '#74E6C1' }, accent: '#74E6C1' },
  { rank: 2, username: 'OrbitJay', coins: 34, score: '05:04', avatar: { ...DEFAULT_AVATAR, shirt: '#B49CFF' }, accent: '#B49CFF' },
  { rank: 3, username: 'SolaByte', coins: 26, score: '05:49', avatar: { ...DEFAULT_AVATAR, shirt: '#FF8E83' }, accent: '#FF8E83' },
]

export const EDITOR_STARTER_OBJECTS: WorldObject[] = [
  { id: 'starter-platform', type: 'Platform', position: [0, 0, 0], rotation: [0, 0, 0], scale: [4, 0.5, 4], color: '#243c73', material: 'matte' },
  { id: 'starter-spawn', type: 'Spawn', position: [0, 0.55, 2], rotation: [0, 0, 0], scale: [1, 1, 1], color: '#74E6C1', material: 'glossy' },
  { id: 'starter-coin', type: 'Coin', position: [2, 1.4, 0], rotation: [0, 0, 0], scale: [1, 1, 1], color: '#F3C969', material: 'metallic' },
]
